import { useEffect, useState } from 'react'
import { translate, type Lang } from './i18n'
import type { District, DistrictImport, GeoLayerVersion, Jurisdiction, ShiftTemplate, StaffingTarget } from './api'
import { ShiftTemplatesEditor } from './ShiftTemplates'
import { GeoAreaEditor } from './GeoAreas'
import { useShiftCodes } from './shifts'


/**
 * La configuración de la agencia: dotación objetivo y mapa de sectores.
 *
 * Las dos son datos que solo la agencia conoce —cuántas unidades pone cada división en cada
 * turno, y de quién es cada beat— y que hasta ahora solo existían en el proyecto de datos de
 * demostración. Se editan aquí porque §16.3 pone «políticas» en esta área, y porque nadie debería
 * tener que entrar al servidor para cambiar cómo está organizada su propia policía.
 *
 * Guardar reemplaza la tabla completa, igual que la disponibilidad: una fila que se quita es una
 * que dejó de existir, y mezclando se quedaría para siempre pidiendo gente que ya nadie asigna.
 */
export function AgencyConfig({
  lang, targets, jurisdictions, busy, onSaveTargets, onSaveJurisdictions,
  shiftTemplates, onSaveShiftTemplates, districts, onUploadDistricts, onDistrictsLoaded,
  zones, onUploadZones, onZonesLoaded, districtVersions, zoneVersions, canRules = true, canLayers = true,
}: {
  /** Turnos, dotación y sectores: los cambia el jefe (H-65). Sin esto se ven y no se editan. */
  canRules?: boolean
  /** Distritos y zonas: los carga el analista (H-65). */
  canLayers?: boolean
  districtVersions?: () => Promise<GeoLayerVersion[]>
  zoneVersions?: () => Promise<GeoLayerVersion[]>
  lang: Lang
  targets: StaffingTarget[]
  jurisdictions: Jurisdiction[]
  busy: boolean
  onSaveTargets: (targets: StaffingTarget[]) => void
  onSaveJurisdictions: (map: Jurisdiction[]) => void
  shiftTemplates: ShiftTemplate[]
  onSaveShiftTemplates: (templates: ShiftTemplate[]) => Promise<boolean>
  districts: District[]
  onUploadDistricts: (geoJson: string, validFrom?: string) => Promise<DistrictImport>
  onDistrictsLoaded: () => void
  zones: District[]
  onUploadZones: (geoJson: string, validFrom?: string) => Promise<DistrictImport>
  onZonesLoaded: () => void
}) {
  const [filas, setFilas] = useState<StaffingTarget[]>(targets)
  const shifts = useShiftCodes()
  const [sectores, setSectores] = useState<Jurisdiction[]>(jurisdictions)

  // Lo que llega del servidor manda mientras no se esté editando algo distinto.
  useEffect(() => setFilas(targets), [targets])
  useEffect(() => setSectores(jurisdictions), [jurisdictions])

  const cambiar = (i: number, cambio: Partial<StaffingTarget>) =>
    setFilas(filas.map((f, j) => (j === i ? { ...f, ...cambio } : f)))

  const numero = (i: number, campo: keyof StaffingTarget, valor: number) =>
    <input
      type="number" min={0} value={valor} style={{ width: 70 }} disabled={busy}
      onChange={(e) => cambiar(i, { [campo]: Number(e.target.value) })}
      aria-label={translate(lang, `config.${campo === 'positions' ? 'positions'
        : campo === 'oneOfficerUnits' ? 'oneOfficer'
        : campo === 'twoOfficerUnits' ? 'twoOfficer' : 'sergeantUnits'}`)}
    />

  return (
    <>
      {/* Primero los turnos: de ellos cuelga todo lo demás de esta pantalla. La dotación se
          declara **por turno**, así que configurar turnos que no existen es el orden al revés. */}
      {!canRules && <p className="notice">{translate(lang, 'config.rulesByChief')}</p>}
      <fieldset className="plain" disabled={!canRules}>
      <ShiftTemplatesEditor
        lang={lang} templates={shiftTemplates} busy={busy} onSave={onSaveShiftTemplates}
      />
      </fieldset>

      {/* Los límites van junto a los sectores, que es lo que vienen a mejorar: el mapa de
          prefijos sigue siendo el respaldo y conviene verlos en la misma pantalla. */}
      {!canLayers && <p className="notice">{translate(lang, 'config.layersByAnalyst')}</p>}
      <fieldset className="plain" disabled={!canLayers}>
      <GeoAreaEditor
        lang={lang} prefix="districts" areas={districts} busy={busy}
        onUpload={onUploadDistricts} onLoaded={onDistrictsLoaded} loadVersions={districtVersions}
      />

      {/* Y las zonas debajo, que es el mismo archivo con otro propósito: los límites dicen de
          quién es el trabajo, las zonas dicen dónde se hace. */}
      <GeoAreaEditor
        lang={lang} prefix="zones" areas={zones} busy={busy}
        onUpload={onUploadZones} onLoaded={onZonesLoaded} loadVersions={zoneVersions}
      />
      </fieldset>

      <fieldset className="plain" disabled={!canRules}>

    <section className="panel">
      <h2>{translate(lang, 'config.title')}</h2>
      <p className="notice">{translate(lang, 'config.help')}</p>

      <div className="card">
        <h3>{translate(lang, 'config.staffing')}</h3>
        <p className="notice">{translate(lang, 'config.staffingHelp')}</p>
        <table className="compare">
          <thead>
            <tr>
              <th>{translate(lang, 'config.division')}</th>
              <th>{translate(lang, 'config.shift')}</th>
              <th>{translate(lang, 'config.positions')}</th>
              <th>{translate(lang, 'config.oneOfficer')}</th>
              <th>{translate(lang, 'config.twoOfficer')}</th>
              <th>{translate(lang, 'config.sergeantUnits')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={`${f.division}-${f.shiftCode}-${i}`}>
                <td>
                  <input value={f.division} disabled={busy} style={{ width: 110 }}
                    onChange={(e) => cambiar(i, { division: e.target.value })}
                    aria-label={translate(lang, 'config.division')} />
                </td>
                <td>
                  <select value={f.shiftCode} disabled={busy}
                    onChange={(e) => cambiar(i, { shiftCode: e.target.value })}
                    aria-label={translate(lang, 'config.shift')}>
                    {shifts.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="num">{numero(i, 'positions', f.positions)}</td>
                <td className="num">{numero(i, 'oneOfficerUnits', f.oneOfficerUnits)}</td>
                <td className="num">{numero(i, 'twoOfficerUnits', f.twoOfficerUnits)}</td>
                <td className="num">{numero(i, 'sergeants', f.sergeants)}</td>
                <td>
                  <button disabled={busy} onClick={() => setFilas(filas.filter((_, j) => j !== i))}>
                    {translate(lang, 'config.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="notice">{translate(lang, 'config.replaces')}</p>
        <div className="actions">
          <button
            disabled={busy}
            onClick={() => setFilas([...filas, {
              division: '', shiftCode: 'EVENING', positions: 0,
              oneOfficerUnits: 0, twoOfficerUnits: 0, sergeants: 0, units: 0,
            }])}
          >
            {translate(lang, 'config.add')}
          </button>
          <button className="primary" disabled={busy} onClick={() => onSaveTargets(filas)}>
            {translate(lang, 'config.save')}
          </button>
        </div>
      </div>

      <div className="card">
        <h3>{translate(lang, 'config.sectors')}</h3>
        <p className="notice">{translate(lang, 'config.sectorsHelp')}</p>
        <table className="compare">
          <thead>
            <tr>
              <th>{translate(lang, 'config.sector')}</th>
              <th>{translate(lang, 'config.division')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sectores.map((s, i) => (
              <tr key={`${s.sector}-${i}`}>
                <td>
                  <input value={s.sector} disabled={busy} style={{ width: 70 }}
                    onChange={(e) => setSectores(sectores.map((x, j) =>
                      (j === i ? { ...x, sector: e.target.value.toUpperCase() } : x)))}
                    aria-label={translate(lang, 'config.sector')} />
                </td>
                <td>
                  <input value={s.division} disabled={busy} style={{ width: 130 }}
                    onChange={(e) => setSectores(sectores.map((x, j) =>
                      (j === i ? { ...x, division: e.target.value } : x)))}
                    aria-label={translate(lang, 'config.division')} />
                </td>
                <td>
                  <button disabled={busy} onClick={() => setSectores(sectores.filter((_, j) => j !== i))}>
                    {translate(lang, 'config.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="actions">
          <button disabled={busy} onClick={() => setSectores([...sectores, { sector: '', division: '' }])}>
            {translate(lang, 'config.add')}
          </button>
          <button className="primary" disabled={busy} onClick={() => onSaveJurisdictions(sectores)}>
            {translate(lang, 'config.save')}
          </button>
        </div>
      </div>
    </section>
      </fieldset>
    </>
  )
}
